import * as cookie from 'tiny-cookie';
import {EventBus} from '../utils';
import { i18nValidation } from '../localize';
const ACTIVE_CAMPGROUND_KEY = 'ACTIVE_CAMPGROUND';
const LANGUAGE_KEY = 'LANGUAGE';

const BVCD_LOGIN_URL = process.env.BVCD_LOGIN_URL || '';
const HAS_BVCD = !!process.env.HAS_BVCD;
const SERVICE_EMAIL = process.env.SERVICE_EMAIL || 'support@campnerd.camp';

class AppModel {
    constructor() {
        this._data = {
            onlyDayPrices: false,
            stripeConfigured: false,
            user: null,
            features: null,
            campgrounds: null,
            checks: null,
            seasons: null,
            persons: null,
            billingTypes: null,
            extraTypes: null,
            extraSubTypes: null,
            campsitesTypes: null,
            stayLoggedIn: false,
            settings: null,
            languages: [],
            countries: [],
            currencies: [],
            portals: [],
            lang: null,
            minPaymentAge: 0,
            validBankCountries: [],
            depositInfo: null,
            surfaces: [],
            target_types: [],
            topx_attributes: [],
            systemLanguages: {
                'de': 'Deutsch',
                'en': 'English',
                // 'es': 'Español',
                // 'fr': 'Français',
                // 'it': 'Italiano',
                // 'nl': 'Nederlands',
            },
        };
    }

    get data() {
        return this._data;
    }

    get user() {
        return this._data.user;
    }

    get onlyDayPrices() {
        return this._data.onlyDayPrices;
    }

    get stripeConfigured() {
        return this._data.stripeConfigured;
    }

    get features() {
        return this._data.features;
    }

    get campgrounds() {
        return this._data.campgrounds;
    }

    get languages() {
        return this._data.languages;
    }

    get countries() {
        return this._data.countries;
    }

    get systemLanguages() {
        return this._data.systemLanguages;
    }

    get surfaces() {
        return this._data.surfaces;
    }

    get topXAttributes() {
        return this._data.topx_attributes;
    }

    get targetTypes() {
        return this._data.target_types;
    }

    get languagesSorted() {
        if(!this.activeCampground) {
            return this.languages;
        }

        const baseLang = this.activeCampground.settings.base_language;
        let langs = this.languages.slice().sort((a, b) => {
            if(a.code === baseLang) {
                return -1;
            }
            if(b.code === baseLang) {
                return 1;
            }

            if(a.code === 'en') {
                return -1;
            }

            if(b.code === 'en') {
                return 1;
            }

            if(a.code > b.code) {
                return 1;
            } else if(a.code < b.code) {
                return -1;
            }
            return 0;
        });

        return langs;
    }

    get currencies() {
        return this._data.currencies;
    }

    get lang() {
        let lang = cookie.get(LANGUAGE_KEY);
        if(lang) {
            return lang;
        }
        if(this._data.lang) {
            return this._data.lang;
        }

        if(!this.activeCampground) {
            return 'de';
        }
        return this.activeCampground.settings.base_language;
    }

    get hasContract() {
        if(!this.activeCampground) {
            return false;
        };
        return !!this.activeCampground.contract_type;
    }

    get isPublished() {
        if(!this.activeCampground) {
            return false;
        };
        return !!this.activeCampground.published;
    }

    get checks() {
        if(this._data.checks) {
            return this._data.checks[cookie.get(ACTIVE_CAMPGROUND_KEY)];
        }
    }

    get allChecksOkay() {
        if(this.checks) {
            let allChecksOkay = true;
            Object.values(this.checks).forEach((check) => {
                if(!check) {
                    allChecksOkay = false;
                }
            });
            return allChecksOkay;
        }
        return false;
    }

    get settingsCheckOkay() {
        if(!this.checks) {
            return false;
        }
        return this.checks.settings === true;
    }

    get activeCampground() {
        return this.getCampgroundBySlug(cookie.get(ACTIVE_CAMPGROUND_KEY));
    }

    get bvcdLoginUrl() {
        return BVCD_LOGIN_URL;
    }

    get hasBvcd() {
        return HAS_BVCD;
    }

    get serviceEmail() {
        return SERVICE_EMAIL;
    }

    get serviceEmailLink() {
        return `<a href="mailto:${this.serviceEmail}">${this.serviceEmail}</a>`;
    }

    get validBankCountries() {
        return this._data.validBankCountries;
    }

    get depositInfo() {
        return this._data.depositInfo;
    }

    get datepickerOptions() {
        return {
            wrap: true,
            altInput: true,
            altFormat: i18nValidation.t('generic.calendar.dateformat'),
            dateFormat: 'Y-m-d',
            disableMobile: true,
            locale: {
                firstDayOfWeek: 1,
                weekdays: {
                    shorthand: i18nValidation.t('generic.calendar.weeknames.short'),
                    longhand: i18nValidation.t('generic.calendar.weeknames.long')
                },
                months: {
                    shorthand: i18nValidation.t('generic.calendar.monthnames.short'),
                    longhand: i18nValidation.t('generic.calendar.monthnames.long')
                }
            },
        };
    }

    get persons() {
        return this._data.persons;
    }

    get campsiteTypes() {
        return this._data.campsitesTypes;
    }

    get billingTypes() {
        return this._data.billingTypes;
    }

    get extraTypes() {
        return this._data.extraTypes;
    }

    get extraSubTypes() {
        return this._data.extraSubTypes;
    }

    get stayLoggedIn() {
        return this._data.stayLoggedIn;
    }

    get minPaymentAge() {
        return this._data.minPaymentAge;
    }

    get settings() {
        return this._data.settings;
    }

    get portals() {
        return this._data.portals;
    }

    setLang(lang) {
        if(lang && lang !== this._data.lang) {
            this._data.lang = lang;
            cookie.set(LANGUAGE_KEY, lang, {expires: '1Y'});
            EventBus.$emit(EventBus.SWITCH_LANGUAGE_START, {lang: lang});
        }
    }

    getDatepickerOptions(config) {
        let opts = Object.assign({}, this.datepickerOptions);
        return Object.assign(opts, config);
    }

    setOnlyDayPrices(onlyDayPrices) {
        this._data.onlyDayPrices = onlyDayPrices;
    }

    setStripeConfigured(stripeConfigured) {
        this._data.stripeConfigured = stripeConfigured;
    }

    setUser(user) {
        this._data.user = user;
    }

    setFeatures(features) {
        this._data.features = features;
    }

    setCampgrounds(campgrounds) {
        this._data.campgrounds = campgrounds;
        if(!this.activeCampground) {
            this.setActiveCampground(campgrounds[0].slug);
        } else {
            this.setCampsiteTypes(this.activeCampground.campsite_types);
        }
    }

    setActiveCampground(campgroundSlug) {
        cookie.set(ACTIVE_CAMPGROUND_KEY, campgroundSlug, {expires: '1M'});
        const campground = this.getCampgroundBySlug(campgroundSlug);
        this.setCampsiteTypes(campground.campsite_types);
        EventBus.$emit(EventBus.CAMPGROUND_CHANGED, campground);
        return campground;
    }

    setChecks(checks) {
        this._data.checks = checks;
    }

    setValidBankCountries(lst) {
        this._data.validBankCountries = lst;
    }

    setChecksForCampground(checks, campgroundSlug = null) {
        if(!campgroundSlug) {
            campgroundSlug = this.activeCampground.slug;
        }
        if(!this._data.checks) {
            this._data.checks = {};
        }
        this._data.checks[campgroundSlug] = checks;
    }

    setPersons(persons) {
        this._data.persons = persons;
    }

    setCampsiteTypes(types) {
        this._data.campsitesTypes = types;
    }

    setBillingTypes(types) {
        this._data.billingTypes = types;
    }

    setExtraTypes(types) {
        this._data.extraTypes = types;
    }

    setExtraSubTypes(types) {
        this._data.extraSubTypes = types;
    }

    setCurrencies(currencies) {
        this._data.currencies = currencies;
    }

    setMinPaymentAge(age) {
        this._data.minPaymentAge = age;
    }

    setLanguages(languages) {
        this._data.languages = languages;
    }

    setCountries(countries) {
        this._data.countries = countries;
    }

    setSurfaces(surfaces) {
        this._data.surfaces = surfaces;
    }

    setTopXAttributes(topxAttributes) {
        this._data.topx_attributes = topxAttributes;
    }

    setTargetTypes(targetTypes) {
        this._data.target_types = targetTypes;
    }

    setSettings(settings) {
        this._data.settings = settings;
    }

    setPortals(portals) {
        this._data.portals = portals;
    }

    setDepositInfo(info) {
        this._data.depositInfo = info;
    }

    getCampgroundBySlug(slug) {
        if(!this.campgrounds) {
            return null;
        }
        for(let ground of this.campgrounds) {
            if(ground.slug === slug) {
                return ground;
            }
        }
        return null;
    }

    setStayLoggedIn(val) {
        this._data.stayLoggedIn = val;
    }

    setPendingPayoutData(data) {
        if(!this.activeCampground) {
            return;
        }

        this.activeCampground.pending_payout_data = data;
    }
}

export default new AppModel();



// WEBPACK FOOTER //
// ./src/models/AppModel.js